import { useDevices } from '../lib/api-client'
import { LoadingCard } from '../components/LoadingSpinner'
import { DeviceViewportChart } from '../components/charts/DeviceViewportChart'
import { exportToCSV, exportToJSON } from '../lib/export'
import { useDateRange } from '../components/Layout'

export function Devices() {
  const { startDate, endDate } = useDateRange()
  const devices = useDevices(startDate, endDate)
  const deviceTypeUsage = devices.data?.data.device_types ?? []
  const deviceTypeTotal = deviceTypeUsage.reduce(
    (sum, item) => sum + item.events,
    0
  )

  const handleExportCSV = () => {
    if (devices.data?.data) {
      exportToCSV(devices.data.data.viewports, 'device-viewports')
    }
  }

  const handleExportJSON = () => {
    if (devices.data?.data) {
      exportToJSON(devices.data.data, 'device-analytics')
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-foreground">Device Analytics</h2>
        <div className="flex gap-2">
          <button
            onClick={handleExportCSV}
            disabled={!devices.data}
            className="px-3 py-1 text-sm bg-secondary rounded-lg hover:bg-secondary/80 disabled:opacity-50"
          >
            Export CSV
          </button>
          <button
            onClick={handleExportJSON}
            disabled={!devices.data}
            className="px-3 py-1 text-sm bg-secondary rounded-lg hover:bg-secondary/80 disabled:opacity-50"
          >
            Export JSON
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Viewport Chart */}
        <div className="bg-card rounded-lg border border-border p-6">
          <h3 className="text-lg font-semibold mb-4">Viewport Sizes</h3>
          {devices.isLoading ? (
            <LoadingCard />
          ) : devices.data ? (
            <DeviceViewportChart
              viewports={devices.data.data.viewports}
              browsers={devices.data.data.browsers}
            />
          ) : (
            <p className="text-muted-foreground">No data available</p>
          )}
        </div>

        {/* Browser Distribution */}
        <div className="bg-card rounded-lg border border-border p-6">
          <h3 className="text-lg font-semibold mb-4">Browser Distribution</h3>
          {devices.isLoading ? (
            <LoadingCard />
          ) : devices.data?.data.browsers.length ? (
            <div className="space-y-3">
              {devices.data.data.browsers.map((browser, i) => {
                const total = devices.data!.data.browsers.reduce(
                  (sum, b) => sum + b.events,
                  0
                )
                const percentage = (browser.events / total) * 100
                return (
                  <div key={i}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-medium">{browser.browser}</span>
                      <span className="text-muted-foreground">
                        {browser.events.toLocaleString()} ({percentage.toFixed(1)}
                        %)
                      </span>
                    </div>
                    <div className="h-2 bg-secondary rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary rounded-full"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <p className="text-muted-foreground">No data available</p>
          )}
        </div>
      </div>

      {/* Device Type Usage */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold mb-4">Device Type Usage</h3>
        {devices.isLoading ? (
          <LoadingCard />
        ) : deviceTypeUsage.length ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {deviceTypeUsage.map((item) => (
              <div
                key={item.device_type}
                className="rounded-lg border border-border/60 bg-secondary/30 p-4"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">
                      {item.device_type}
                    </p>
                    <p className="text-2xl font-semibold text-foreground">
                      {item.events.toLocaleString()}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {item.unique_visitors.toLocaleString()} unique visitors
                    </p>
                  </div>
                  <span className="text-sm font-medium text-foreground">
                    {deviceTypeTotal
                      ? ((item.events / deviceTypeTotal) * 100).toFixed(1)
                      : '0.0'}
                    %
                  </span>
                </div>
                <div className="mt-3 h-2 bg-secondary rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary rounded-full"
                    style={{
                      width: `${
                        deviceTypeTotal ? (item.events / deviceTypeTotal) * 100 : 0
                      }%`,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-muted-foreground">No data available</p>
        )}
      </div>

      {/* Viewport Table */}
      <div className="bg-card rounded-lg border border-border overflow-hidden">
        <table className="w-full">
          <thead className="bg-secondary/50">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                Device Category
              </th>
              <th className="px-4 py-3 text-right text-sm font-medium text-muted-foreground">
                Events
              </th>
              <th className="px-4 py-3 text-right text-sm font-medium text-muted-foreground">
                Unique Visitors
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {devices.isLoading ? (
              <tr>
                <td colSpan={3} className="px-4 py-8 text-center">
                  <div className="flex justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
                  </div>
                </td>
              </tr>
            ) : devices.data?.data.viewports.length ? (
              devices.data.data.viewports.map((row, i) => (
                <tr key={i} className="hover:bg-secondary/30">
                  <td className="px-4 py-3 text-sm font-medium">
                    {row.device_category}
                  </td>
                  <td className="px-4 py-3 text-sm text-right">
                    {row.events.toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-sm text-right">
                    {row.unique_visitors.toLocaleString()}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={3}
                  className="px-4 py-8 text-center text-muted-foreground"
                >
                  No data available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
